import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

interface UseParallaxOptions {
    speed?: number;
    rootMargin?: string;
}

/**
 * Lightweight scroll parallax hook for media sections.
 */
export const useParallax = <T extends HTMLElement>({
    speed = 0.1,
    rootMargin = '200px 0px',
}: UseParallaxOptions = {}) => {
    const [offsetY, setOffsetY] = useState(0);
    const elementRef = useRef<T | null>(null);
    const rafRef = useRef<number | null>(null);
    const { ref: observeRef, inView } = useInView({
        threshold: 0,
        rootMargin,
    });

    const setRefs = useCallback(
        (node: T | null) => {
            elementRef.current = node;
            observeRef(node);
        },
        [observeRef],
    );

    useEffect(() => {
        if (!inView) {
            return;
        }

        const updateOffset = () => {
            const node = elementRef.current;
            if (!node) {
                return;
            }

            const rect = node.getBoundingClientRect();
            const viewportMidpoint = window.innerHeight / 2;
            const elementMidpoint = rect.top + rect.height / 2;
            setOffsetY((viewportMidpoint - elementMidpoint) * speed);
        };

        const scheduleUpdate = () => {
            if (rafRef.current !== null) {
                return;
            }

            rafRef.current = window.requestAnimationFrame(() => {
                updateOffset();
                rafRef.current = null;
            });
        };

        updateOffset();
        window.addEventListener('scroll', scheduleUpdate, { passive: true });
        window.addEventListener('resize', scheduleUpdate);

        return () => {
            if (rafRef.current !== null) {
                window.cancelAnimationFrame(rafRef.current);
            }
            window.removeEventListener('scroll', scheduleUpdate);
            window.removeEventListener('resize', scheduleUpdate);
        };
    }, [inView, speed]);

    return {
        ref: setRefs,
        offsetY,
    };
};
